import React, { useCallback, useEffect, useState } from 'react';
import {
  AccountingPeriodReturn,
  BudgetLineReturn,
  FundReturn,
  GlAccountReturn,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatMoney,
  moneyToCents,
  centsToMoney,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
  canManage: boolean;
}

/** A row being edited: period number → the string in the box. */
type Draft = Record<number, string>;

const keyOf = (line: { account_id: string; fund_id: string }): string =>
  `${line.account_id}:${line.fund_id}`;

/**
 * The budget grid: account down, period across.
 *
 * **There is no annual input box.** The annual figure is the sum of the
 * periods, computed on read — storing both would be two sources of
 * truth for one number, and the one that drifts is always the one on
 * the report. An even spread is offered as a *fill* action instead: it
 * is a data-entry convenience, and what gets stored is always the
 * twelve figures.
 *
 * Snow removal is why. Six winter months at 1,333.33 and six summer
 * months at zero cannot be expressed by a model that stores a year and
 * divides, and seasonal spending is most of what a maintenance budget
 * is.
 */
const BudgetGrid: React.FC<Props> = ({ hoaId, canManage }) => {
  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [periodNumbers, setPeriodNumbers] = useState<number[]>([]);
  const [lines, setLines] = useState<BudgetLineReturn[]>([]);
  const [accounts, setAccounts] = useState<GlAccountReturn[]>([]);
  const [funds, setFunds] = useState<FundReturn[]>([]);

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [spreading, setSpreading] = useState<BudgetLineReturn | null>(null);
  const [spreadAmount, setSpreadAmount] = useState('');
  const [adding, setAdding] = useState(false);
  const [newAccount, setNewAccount] = useState('');
  const [newFund, setNewFund] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [p, a, f] = await Promise.all([
          ledgerService.listPeriods({ hoaId }),
          ledgerService.listAccounts({ hoaId }),
          ledgerService.listFunds({ hoaId }),
        ]);
        const real = p.data.periods.filter(
          (x: AccountingPeriodReturn) => !x.is_opening
        );
        const ys = Array.from(new Set(real.map((x) => x.fiscal_year))).sort();
        setYears(ys);
        setYear(ys[ys.length - 1] ?? null);
        setAccounts(a.data.accounts.filter((x) => x.is_active));
        setFunds(f.data.funds.filter((x) => x.is_active));
      } catch (err) {
        setError(billingErrorMessage(err, "Couldn't load the budget."));
        setLoading(false);
      }
    };
    void load();
  }, [hoaId]);

  const load = useCallback(async () => {
    if (year === null) return;
    try {
      setLoading(true);
      const [b, p] = await Promise.all([
        ledgerService.getBudget({ hoaId, fiscalYear: year }),
        ledgerService.listPeriods({ hoaId }),
      ]);
      setLines(b.data.lines);
      setPeriodNumbers(
        p.data.periods
          .filter((x) => !x.is_opening && x.fiscal_year === year)
          .map((x) => x.period_number)
          .sort((m, n) => m - n)
      );
      setDrafts({});
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't load the budget."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const draftFor = (line: BudgetLineReturn): Draft => {
    const existing = drafts[keyOf(line)];
    if (existing) return existing;
    const d: Draft = {};
    for (const n of periodNumbers) {
      d[n] = line.periods.find((p) => p.period_number === n)?.amount ?? '';
    }
    return d;
  };

  const setCell = (line: BudgetLineReturn, period: number, value: string) =>
    setDrafts((prev) => ({
      ...prev,
      [keyOf(line)]: { ...draftFor(line), [period]: value },
    }));

  /** Summed from the boxes, never typed. */
  const draftAnnual = (line: BudgetLineReturn): string => {
    const d = draftFor(line);
    let cents = BigInt(0);
    for (const n of periodNumbers) {
      const raw = (d[n] ?? '').trim();
      if (!raw) continue;
      try {
        cents += moneyToCents(raw);
      } catch {
        return '—';
      }
    }
    return centsToMoney(cents);
  };

  const dirty = (line: BudgetLineReturn): boolean => !!drafts[keyOf(line)];

  const saveLine = (line: BudgetLineReturn) => {
    const d = draftFor(line);
    const periods: Record<string, string> = {};
    for (const n of periodNumbers) {
      const raw = (d[n] ?? '').trim();
      if (raw) periods[String(n)] = raw;
    }
    void (async () => {
      try {
        setBusy(true);
        setError(null);
        await ledgerService.setBudget({
          hoaId,
          fiscalYear: year as number,
          budgetYearSet: {
            lines: [
              {
                account_id: line.account_id,
                fund_id: line.fund_id,
                periods,
                annual: null,
              },
            ],
          },
        });
        await load();
      } catch (err) {
        setError(billingErrorMessage(err, "Couldn't save that line."));
      } finally {
        setBusy(false);
      }
    })();
  };

  const applySpread = () => {
    if (!spreading) return;
    void (async () => {
      try {
        setBusy(true);
        setError(null);
        await ledgerService.setBudget({
          hoaId,
          fiscalYear: year as number,
          budgetYearSet: {
            lines: [
              {
                account_id: spreading.account_id,
                fund_id: spreading.fund_id,
                // The spread is the backend's, not ours: the rounding
                // remainder lands in the last period so the twelve sum
                // to the annual figure exactly. Dividing here would put
                // 7,999.98 on a report that says 8,000.00.
                annual: spreadAmount.trim(),
              },
            ],
          },
        });
        setSpreading(null);
        setSpreadAmount('');
        await load();
      } catch (err) {
        setError(billingErrorMessage(err, "Couldn't spread that."));
      } finally {
        setBusy(false);
      }
    })();
  };

  const addLine = () => {
    const account = accounts.find((a) => a.id === newAccount);
    const fund = funds.find((f) => f.id === newFund);
    if (!account || !fund) return;
    setLines((prev) => [
      ...prev,
      {
        account_id: account.id,
        account_number: account.account_number,
        account_name: account.name,
        fund_id: fund.id,
        fund_code: fund.code,
        annual: '0.00',
        periods: [],
      },
    ]);
    setAdding(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="That did not work"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <FieldPicker
          className="w-full max-w-[12rem]"
          label="Fiscal year"
          value={year === null ? '' : String(year)}
          onChange={(v) => setYear(Number(v))}
          options={years.map((y) => ({ value: String(y), label: `FY${y}` }))}
        />
        {canManage && (
          <Button color="sky" onClick={() => setAdding(true)}>
            Add a line
          </Button>
        )}
      </div>

      <Divider className="my-4" />

      {lines.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No budget lines for FY{year}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-zinc-950 dark:text-white">
            <thead>
              <tr className="text-xs text-zinc-500 dark:text-zinc-400">
                <th className="text-left py-2 pr-3">Account</th>
                <th className="text-left py-2 pr-3">Fund</th>
                {periodNumbers.map((n) => (
                  <th key={n} className="text-right py-2 px-1 min-w-[6rem]">
                    P{n}
                  </th>
                ))}
                <th className="text-right py-2 pl-3 w-28">Annual</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const d = draftFor(line);
                return (
                  <tr
                    key={keyOf(line)}
                    className="border-t border-zinc-100 dark:border-zinc-800"
                  >
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {line.account_number} {line.account_name}
                    </td>
                    <td className="py-1 pr-3">{line.fund_code}</td>
                    {periodNumbers.map((n) => (
                      <td key={n} className="py-1 px-1 min-w-[6rem]">
                        <Input
                          className="text-right tabular-nums"
                          aria-label={`${line.account_number} ${line.fund_code} period ${n}`}
                          value={d[n] ?? ''}
                          disabled={!canManage}
                          onChange={(e) => setCell(line, n, e.target.value)}
                        />
                      </td>
                    ))}
                    {/*
                      Computed, never an input. Offering one to type
                      would be a second source of truth, and the one
                      that drifts is the one on the report.
                    */}
                    <td className="py-1 pl-3 text-right tabular-nums font-medium">
                      {formatMoney(draftAnnual(line))}
                    </td>
                    <td className="py-1 pl-2 whitespace-nowrap">
                      {canManage && (
                        <>
                          <Button
                            plain
                            onClick={() => {
                              setSpreading(line);
                              setSpreadAmount('');
                            }}
                          >
                            Spread…
                          </Button>
                          <Button
                            plain
                            disabled={busy || !dirty(line)}
                            onClick={() => saveLine(line)}
                          >
                            Save
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
        Saving a line replaces that account and fund's whole year. A period left
        blank is set to nothing — which is how "no snow removal after March" is
        expressed.
      </p>

      <Dialog open={!!spreading} onClose={() => setSpreading(null)}>
        <DialogTitle>Spread a year evenly</DialogTitle>
        <DialogDescription>
          {spreading?.account_number} {spreading?.account_name} ·{' '}
          {spreading?.fund_code}
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Annual figure</Label>
                <Input
                  value={spreadAmount}
                  onChange={(e) => setSpreadAmount(e.target.value)}
                  placeholder="8000.00"
                />
                <Description>
                  Divided across {periodNumbers.length} periods, with the
                  rounding remainder in the last so they sum to it exactly. This
                  replaces the year's figures for this line — a seasonal budget
                  should be typed into the periods instead.
                </Description>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setSpreading(null)}>
            Cancel
          </Button>
          <Button
            color="sky"
            disabled={busy || !spreadAmount.trim()}
            onClick={applySpread}
          >
            Spread
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adding} onClose={() => setAdding(false)}>
        <DialogTitle>Add a budget line</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Account</Label>
                <FieldPicker
                  label="Account"
                  value={newAccount}
                  onChange={setNewAccount}
                  placeholder="Choose an account…"
                  options={accounts.map((a) => ({
                    value: a.id,
                    label: `${a.account_number} ${a.name}`,
                  }))}
                />
              </Field>
              <Field>
                <Label>Fund</Label>
                <FieldPicker
                  label="Fund"
                  value={newFund}
                  onChange={setNewFund}
                  placeholder="Choose a fund…"
                  options={funds.map((f) => ({ value: f.id, label: f.name }))}
                />
                {/*
                  Per account, per fund. The same GL account is
                  legitimately budgeted in both Operating and Reserve —
                  60200 Administration appears under each in a real
                  packet.
                */}
                <Description>
                  The same account can be budgeted in more than one fund.
                </Description>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setAdding(false)}>
            Cancel
          </Button>
          <Button
            color="sky"
            disabled={!newAccount || !newFund}
            onClick={addLine}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BudgetGrid;
